import { useEffect, useMemo, useState } from "react";
import { createForm } from "@formily/core";
import { useQuery } from "@tanstack/react-query";
import SchemaFormView from "@/components/Schema/SchemaFormView";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { notify } from "@/components/common/Notification";
import { normalizeDynamicModuleUploadFields } from "@/lib/normalizeDynamicModuleUploadFields";
import { removeMetaFields } from "@/lib/removeMetaFields";
import ServiceDeskAPIService from "@sd/services/APIs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  SERVICE_DESK_FORMILY_TICKET_MODEL,
  serviceDeskFormilyTicketGridQueryKey,
} from "@sd/services/APIs";
import { useQueryClient } from "@tanstack/react-query";
import { useUser } from "@/context/useUser";
import { getUserInfo } from "@/lib/storeManager";

export interface ServiceDeskTicketSchemaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** After successful Formily create + cache invalidation */
  onCreated?: () => void;
}

export function ServiceDeskTicketSchemaDialog({
  open,
  onOpenChange,
  onCreated,
}: ServiceDeskTicketSchemaDialogProps) {
  const queryClient = useQueryClient();
  const { decodedToken } = useUser();
  const [submitting, setSubmitting] = useState(false);
  const [formInstanceKey, setFormInstanceKey] = useState(0);
  const form = useMemo(
    () => createForm({}),
    [formInstanceKey]
  );

  const { data: schemaInfo, isLoading: schemaLoading } = useQuery({
    queryKey: ["schema", SERVICE_DESK_FORMILY_TICKET_MODEL],
    queryFn: () =>
      ServiceDeskAPIService.schema.getByName(SERVICE_DESK_FORMILY_TICKET_MODEL),
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setFormInstanceKey(k => k + 1);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!schemaInfo) {
      return;
    }
    try {
      await form.validate();
    } catch {
      notify.error("Validation", "Please fix the highlighted fields.");
      return;
    }

    let values2 = removeMetaFields(form.values) as Record<string, unknown>;
    values2 = normalizeDynamicModuleUploadFields(
      schemaInfo?.json ?? schemaInfo,
      values2
    );

    const storedUser = getUserInfo() as {
      id?: string;
      user_id?: string;
    } | null;
    const tokenUser = decodedToken as {
      user_id?: string;
      userId?: string;
    } | null;
    const loginUserId =
      storedUser?.id ??
      storedUser?.user_id ??
      tokenUser?.user_id ??
      tokenUser?.userId;
    const fromForm =
      typeof values2.requester === "string" && values2.requester.trim()
        ? values2.requester.trim()
        : undefined;
    const requester = fromForm ?? loginUserId;
    if (!requester) {
      notify.error(
        "Requester required",
        "No requester in form and no user id on token. Cannot create ticket."
      );
      return;
    }
    const values = { ...values2, requester };

    setSubmitting(true);
    try {
      await ServiceDeskAPIService.formily.createItem(
        SERVICE_DESK_FORMILY_TICKET_MODEL,
        values
      );
      await queryClient.invalidateQueries({
        queryKey: serviceDeskFormilyTicketGridQueryKey,
      });
      onCreated?.();
      onOpenChange(false);
      notify.success("Ticket created", "Saved via ServiceDesk Ticket schema.");
    } catch (e) {
      notify.error(
        "Create failed",
        e instanceof Error ? e.message : "Could not create ticket."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // Radix modal dialogs set pointer-events: none on the page; Ant/Formily Select
    // menus render in a body portal, so they cannot receive clicks unless the
    // dialog is non-modal. Overlay still dims the background.
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent className="flex max-h-[min(90vh,900px)] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
        <DialogHeader className="shrink-0 border-b px-6 py-4 text-left">
          <DialogTitle>New ticket</DialogTitle>
          <DialogDescription className="sr-only">
            Create a new service desk ticket.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-[240px] flex-1 overflow-y-auto px-6 py-4">
          {schemaLoading || !schemaInfo ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : (
            <SchemaFormView
              key={formInstanceKey}
              form={form}
              schemaJson={schemaInfo?.json ?? schemaInfo}
            />
          )}
        </div>

        <DialogFooter className="shrink-0 border-t px-6 py-4">
          <Button
            type="button"
            variant="outline"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={submitting || schemaLoading || !schemaInfo}
            onClick={() => void handleSubmit()}
          >
            {submitting ? "Saving…" : "Create ticket"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
